/**
 * Redux → AccountStore legacy bridge (skeleton)
 *
 * Purpose:
 * - Provide a single place to later subscribe to Redux store changes
 *   and re-emit legacy AccountStore events (emitChange, emitBalanceChanged,
 *   emitCreditCardAdded/Deleted/DefaultUpdated, emitHistoryChanged, etc.).
 * - During migration, this allows class components relying on AccountStore
 *   events to keep working without changing their imports.
 *
 * IMPORTANT: This is a no-op skeleton. It does not subscribe or emit anything
 * until explicitly enabled. Safe to keep in the repo without side effects.
 */

export type Unsubscribe = () => void;

export type BridgeInitOptions = {
  getState?: () => unknown;

  subscribe?: (listener: () => void) => Unsubscribe;

  enabled?: boolean;
};

let unsubscribeRef: Unsubscribe | null = null;
let isEnabled = false;

export function initReduxAccountBridge(opts: BridgeInitOptions = {}): void {
  internal.bridgeOptions = {
    getState: opts.getState,
    subscribe: opts.subscribe,
  };
  if (opts.enabled) {
    enableReduxAccountBridge();
  }
}

export function enableReduxAccountBridge(): void {
  if (isEnabled) {
    return;
  }
  if (!internal.bridgeOptions.subscribe) {
    return;
  }

  // Initialize prev snapshot from current state to avoid false first-tick emits
  try {
    const st: any = internal.bridgeOptions.getState?.();
    internal.prev.balance = st?.account?.account?.defaultBalance?.balance;
    const cards = st?.account?.creditCards || [];
    internal.prev.cardsLen = Array.isArray(cards) ? cards.length : 0;
    internal.prev.defaultToken = st?.account?.defaultPaymentToken || '';
    internal.prev.pinRequired = st?.account?.pinRequired || false;
    internal.prev.firstName = st?.account?.account?.firstName || '';
    internal.prev.lastName = st?.account?.account?.lastName || '';
    internal.prev.privacyVersion =
      st?.account?.privacyVersionResponse?.version || '';
  } catch {}

  isEnabled = true;
  unsubscribeRef = internal.bridgeOptions.subscribe(() => {
    try {
      const st: any = internal.bridgeOptions.getState?.();

      const nextBalance: number | undefined =
        st?.account?.account?.defaultBalance?.balance;
      if (
        typeof nextBalance === 'number' &&
        nextBalance !== internal.prev.balance
      ) {
        internal.prev.balance = nextBalance;
        try {
          const AccountStore = require('src/stores/AccountStore').default;
          AccountStore.emitBalanceChanged?.();
          AccountStore.emitChange?.();
        } catch {}
      }

      const nextCards: any[] = st?.account?.creditCards || [];
      if (Array.isArray(nextCards)) {
        const nextLen = nextCards.length;
        if (typeof internal.prev.cardsLen !== 'number') {
          internal.prev.cardsLen = nextLen;
        } else if (nextLen !== internal.prev.cardsLen) {
          const added = nextLen > internal.prev.cardsLen;
          internal.prev.cardsLen = nextLen;
          try {
            const AccountStore = require('src/stores/AccountStore').default;
            if (added) {
              AccountStore.emitCreditCardAdded?.();
            } else {
              const isDefault = st?.account?.lastDeletedCardWasDefault || false;
              AccountStore.emitCreditCardDeleted?.(isDefault);
            }
            AccountStore.emitChange?.();
          } catch {}
        }
      }

      const nextDefaultToken: string = st?.account?.defaultPaymentToken || '';
      if (nextDefaultToken !== internal.prev.defaultToken) {
        const prev = internal.prev.defaultToken;
        internal.prev.defaultToken = nextDefaultToken;
        if (typeof prev !== 'undefined') {
          try {
            const AccountStore = require('src/stores/AccountStore').default;
            AccountStore.emitDefaultTokenUpdated?.();
            AccountStore.emitChange?.();
          } catch {}
        }
      }

      const nextPinRequired: boolean = st?.account?.pinRequired || false;
      if (
        typeof internal.prev.pinRequired === 'boolean' &&
        nextPinRequired !== internal.prev.pinRequired
      ) {
        internal.prev.pinRequired = nextPinRequired;
        try {
          const AccountStore = require('src/stores/AccountStore').default;
          AccountStore.emitChange?.();
        } catch {}
      } else if (typeof internal.prev.pinRequired === 'undefined') {
        internal.prev.pinRequired = nextPinRequired;
      }

      const nextFirstName: string = st?.account?.account?.firstName || '';
      const nextLastName: string = st?.account?.account?.lastName || '';
      const profileChanged =
        (typeof internal.prev.firstName === 'string' &&
          nextFirstName !== internal.prev.firstName) ||
        (typeof internal.prev.lastName === 'string' &&
          nextLastName !== internal.prev.lastName);

      if (profileChanged) {
        internal.prev.firstName = nextFirstName;
        internal.prev.lastName = nextLastName;
        try {
          const AccountStore = require('src/stores/AccountStore').default;
          AccountStore.emitChange?.();
        } catch {}
      } else if (
        typeof internal.prev.firstName === 'undefined' ||
        typeof internal.prev.lastName === 'undefined'
      ) {
        internal.prev.firstName = nextFirstName;
        internal.prev.lastName = nextLastName;
      }

      const nextPrivacyVersion: string =
        st?.account?.privacyVersionResponse?.version || '';
      if (
        typeof internal.prev.privacyVersion === 'string' &&
        nextPrivacyVersion !== internal.prev.privacyVersion &&
        nextPrivacyVersion !== ''
      ) {
        internal.prev.privacyVersion = nextPrivacyVersion;
        try {
          const AccountStore = require('src/stores/AccountStore').default;
          AccountStore.emitChange?.();
        } catch {}
      } else if (typeof internal.prev.privacyVersion === 'undefined') {
        internal.prev.privacyVersion = nextPrivacyVersion;
      }
    } catch {}
  });
}

export function disableReduxAccountBridge(): void {
  if (unsubscribeRef) {
    try {
      unsubscribeRef();
    } catch {}
  }
  unsubscribeRef = null;
  isEnabled = false;
}

const internal: {
  bridgeOptions: {
    getState?: () => unknown;
    subscribe?: (listener: () => void) => Unsubscribe;
  };
  prev: {
    balance?: number;
    cardsLen?: number;
    defaultToken?: string;
    pinRequired?: boolean;
    firstName?: string;
    lastName?: string;
    privacyVersion?: string;
  };
} = {
  bridgeOptions: {},
  prev: {},
};
